/**
 * Toaster Component - Displays toast notifications
 *
 * Fixed position container that renders active toasts with smooth Framer Motion animations.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';
import { prefersReducedMotion, toastVariants } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { Icon } from './icon';

export function Toaster() {
  const { toasts, dismiss } = useToast();
  const shouldAnimate = !prefersReducedMotion();

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-md w-full pointer-events-none">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <motion.div
            key={toast.id}
            layout={shouldAnimate}
            variants={shouldAnimate ? toastVariants : undefined}
            initial={shouldAnimate ? 'initial' : false}
            animate="animate"
            exit={shouldAnimate ? 'exit' : undefined}
            className={cn(
              'pointer-events-auto rounded-radius-lg border p-4 shadow-lg',
              toast.variant === 'destructive'
                ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 text-red-900 dark:text-red-100'
                : 'border-border bg-card text-card-foreground',
            )}
          >
            <div className="flex items-start gap-3">
              {/* Status icon */}
              <div
                className={cn(
                  'flex-shrink-0 mt-0.5',
                  toast.variant === 'destructive'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-emerald-600 dark:text-emerald-400',
                )}
              >
                <Icon
                  name={toast.variant === 'destructive' ? 'error' : 'check_circle'}
                  className="h-5 w-5"
                />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{toast.title}</p>
                {toast.description && (
                  <p
                    className={cn(
                      'mt-1 text-sm',
                      toast.variant === 'destructive'
                        ? 'text-red-700 dark:text-red-300'
                        : 'text-muted-foreground',
                    )}
                  >
                    {toast.description}
                  </p>
                )}
              </div>

              <motion.button
                onClick={() => dismiss(toast.id)}
                className={cn(
                  'flex-shrink-0 rounded-radius-md p-1 transition-colors',
                  toast.variant === 'destructive'
                    ? 'hover:bg-red-100 dark:hover:bg-red-900'
                    : 'hover:bg-accent',
                )}
                whileHover={shouldAnimate ? { scale: 1.1 } : undefined}
                whileTap={shouldAnimate ? { scale: 0.95 } : undefined}
              >
                <Icon name="close" className="h-4 w-4" />
              </motion.button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
